// organize-imports-ignore

// import '~/styles/reset.scss'; // Just leave it to Vuetify default settings. No need for `reset.scss`

// Import Yup plugin first to ensure locale is set before any validation schemas are created
import '@/plugins/yup';
import fontawesome from '@/plugins/fontawesome';

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { registerPlugins } from '@/plugins';
import { registerInertiaEventHandler } from '@/inertia-event-handler';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import AppLayout from '@/layouts/AppLayout.vue';
import { getPageMetaTitle, setPageMetaTitle } from '@/pageMeta';
import { formatComponentNameToTitle } from '@/utils/component';
import { appName } from '@/config/app';

// Application CSS or SCSS must be imported last,
// after Vuetify style has been imported inside `resources/js/plugins/vuetify/index.js`
import '~/styles/app.scss';

import './bootstrap';

const pagesWithoutLayout = ['Welcome', 'auth/Login'];
// Track the most recently resolved component name for reliable title fallback
let currentComponentName = null;

createInertiaApp({
  title: (title) => {
    // 1) If a page sets a title explicitly via <Head>, use it
    if (title) return `${appName} | ${title}`;

    // 2) If a page declared meta.title, prefer that without requiring <Head>
    const componentName = currentComponentName;
    if (!componentName) return appName;
    const metaTitle = getPageMetaTitle(componentName);
    if (metaTitle) return `${appName} | ${metaTitle}`;

    // 3) Fallback: derive from component name
    const pageName = formatComponentNameToTitle(componentName);
    return pageName ? `${appName} | ${pageName}` : appName;
  },

  resolve: async (name) => {
    // Save the current component name early so title() can rely on it
    currentComponentName = name;
    const page = await resolvePageComponent(
      `./pages/${name}.vue`,
      import.meta.glob('./pages/**/*.vue')
    );

    if (!pagesWithoutLayout.includes(name)) {
      page.default.layout = page.default.layout || AppLayout;
    }

    // Capture optional meta.title from the page component for title override
    // Supports Options API and <script setup> via defineOptions (both end up on default export)
    const declaredTitle = page?.default?.meta?.title;
    if (declaredTitle) {
      setPageMetaTitle(name, declaredTitle);
    }

    return page;
  },

  setup({ el, App, props, plugin }) {
    const app = createApp({ render: () => h(App, props) });

    // Register Inertia plugin first
    app.use(plugin);

    // Register other plugins
    registerPlugins(app);
    registerInertiaEventHandler(router);
    fontawesome(app);

    app.mount(el);
  },

  progress: {
    color: 'rgb(var(--v-theme-primary))',
  },
});
